import type { QueryFunctionContext } from '@tanstack/react-query';
import type { PreferredMultiple } from '../../selector';
import { gql } from '@/utilities/gql';
import type { ResultOf } from '@/utilities/gql';
import { gqlClient } from '@/utilities/graphQLClient';

const QUERY = gql(
  `
    query GetIndustryHistogramCompanies(
      $limit: Float!
      $industryId: Float!
      $preferredMultiple: String!
      $min: Float
      $max: Float
      $region: String
      $sortBy: String
      $sortOrder: String
      $countryISO: String
    ) {
      CompanyIndustryHistogramCompanies(
        limit: $limit
        industryID: $industryId
        preferredMultiple: $preferredMultiple
        min: $min
        max: $max
        region: $region
        countryISO: $countryISO
        sortBy: $sortBy
        sortOrder: $sortOrder
      ) {
        id
        name
        tickerSymbol
        uniqueSymbol
        canonicalURL
        exchangeCountryISO
        listingCurrencyISO
        analysisValue {
          marketCap
          marketCapUSD
          pe
          pb
          priceToSales
          return1yAbs
        }
        analysisFuture {
          netIncome1Y
          revenue1Y
          netIncomeGrowth1Y
          revenueGrowth1Y
          forwardPe1Y
          forwardPriceToSales1Y
          netIncomeGrowthAnnual
          revenueGrowthAnnual
        }
        country {
          iso2
          region {
            name
          }
        }
        primaryIndustry {
          id
        }
      }
    }
  `
);

// TODO: This should be exported as the hook ReturnType. However, that is not possible as the hooks ReturnType is derived from the query response
// useIndustryCompanies in general needs to a review, a decoupling and refactor.
export type CompanyIndustryHistogramCompanies = ResultOf<
  typeof QUERY
>['CompanyIndustryHistogramCompanies'];

export function getQueryKey(
  preferredMultiple: PreferredMultiple,
  countryISO: string | null,
  region: string | null,
  min: number | null,
  max: number | null,
  industryId: number,
  limit: number
): [
  string,
  string,
  string | null,
  string | null,
  number | null,
  number | null,
  number,
  number,
] {
  return [
    'COMPANY_INDUSTRY_COMPANIES',
    preferredMultiple,
    countryISO,
    region,
    min,
    max,
    industryId,
    limit,
  ];
}

export const queryFn = async ({
  queryKey: [
    _keyof,
    preferredMultiple,
    countryISO,
    region,
    min,
    max,
    industryId,
    limit,
  ],
}: QueryFunctionContext<
  [
    string,
    string,
    string | null,
    string | null,
    number | null,
    number | null,
    number,
    number,
  ]
>) => {
  const response = await gqlClient.request(QUERY, {
    limit,
    industryId,
    preferredMultiple: preferredMultiple.toLowerCase(),
    min,
    max,
    region,
    countryISO,
    sortBy: 'analysisValue.marketCap',
    sortOrder: 'DESC',
  });

  if ('CompanyIndustryHistogramCompanies' in response) {
    return response.CompanyIndustryHistogramCompanies;
  }
};
